import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { OutlookThreadMessage } from '../types';
import OutlookAttachment from './OutlookAttachment';
import OutlookHtmlBody from './OutlookHtmlBody';

type OutlookThreadMessageCardProps = {
  message: OutlookThreadMessage;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  onReply: (message: OutlookThreadMessage) => void;
};

/**
 * A single collapsible message card inside the thread dialog.
 * Collapsed: shows sender header + one-line body preview.
 * Expanded:  shows full HTML body, attachments, and a "Reply in Outlook" button.
 */
const OutlookThreadMessageCard = ({
  message,
  isExpanded,
  onToggle,
  onReply,
}: OutlookThreadMessageCardProps) => {
  const theme = useTheme();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle(message.id);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: 2,
        bgcolor: theme.palette.mode === 'dark' ? '#3A3444' : 'background.paper',
        '& + &': { mt: 1 },
      }}
    >
      {/* ── Clickable header: avatar, sender, date ── */}
      <Box
        display="flex"
        justifyContent="space-between"
        gap={2}
        width="100%"
        minWidth={0}
        role="button"
        tabIndex={0}
        onClick={() => onToggle(message.id)}
        onKeyDown={handleKeyDown}
        sx={{ cursor: 'pointer' }}
      >
        <Box
          display="flex"
          alignItems="flex-start"
          gap={2}
          flex={1}
          minWidth={0}
        >
          <Avatar
            fallbackText={message.senderInitials}
            sx={{
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? '#D7D2DF' : message.senderColor,
              width: 48,
              height: 48,
              fontSize: 20,
              flexShrink: 0,
            }}
          />

          <Box minWidth={0} flex={1}>
            <Box
              display="flex"
              alignItems="baseline"
              gap={1}
              minWidth={0}
              flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color="textPrimary"
                noWrap
                sx={{ flexShrink: 0 }}
              >
                {message.senderName}
              </Typography>

              {message.senderEmail ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  &lt;{message.senderEmail}&gt;
                </Typography>
              ) : null}
            </Box>

            {isExpanded && (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {message.recipients}
              </Typography>
            )}
            {/* ── Collapsed: one-line preview ── */}
            {!isExpanded ? (
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {/* Show plain-text preview even for HTML bodies */}
                {message.bodyPreview}
              </Typography>
            ) : null}
          </Box>
        </Box>

        {isExpanded && (
          <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
            <Typography variant="body2" color="text.secondary" noWrap>
              {message.sentLabel?.replace(/^Today\s*/i, '')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Expanded: full body + attachments + reply ── */}
      {isExpanded ? (
        <>
          {message.attachments?.length ? (
            <Box mt={3}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: '14px',
                  letterSpacing: '1px',
                  color: theme.palette.mode === 'dark' ? '#FFFFFF' : undefined,
                }}
              >
                {message.attachments.length} ATTACHMENT
                {message.attachments.length > 1 ? 'S' : ''}
              </Typography>

              <Box display="flex" flexDirection="column" gap={1.5} mt={2}>
                {message.attachments.map((attachment) => (
                  <OutlookAttachment
                    key={attachment.id}
                    attachment={attachment}
                  />
                ))}
              </Box>
            </Box>
          ) : null}

          <Box mt={3}>
            <OutlookHtmlBody html={message.body} isHtml={message.bodyIsHtml} />
          </Box>

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button
              kind="primary"
              onClick={() => onReply(message)}
              startIcon={<OpenInNewIcon />}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Reply in Outlook
            </Button>
          </Box>
        </>
      ) : null}
    </Box>
  );
};

export default OutlookThreadMessageCard;
